/* Screen 2 - Review & Confirm */

import { unwrapResult } from "@reduxjs/toolkit";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import SummaryContent from "../../../../components/common/SummaryContent/SummaryContent";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import { selectProfileData } from "../../../../redux/features/profileSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import { applyPromoCodeToQuote, calculatePromoDiscount } from "../../../../utils/promoCodeUtils";
import { policyPay, getPolicyPayLoading } from "../../../../redux/features/payment/policyPaySlice";
import RenewalLayout from "../../components/RenewalLayout";

const ReviewConfirmPage = ({
  policyDetailsInfo,
  formData,
  onBack,
  premiumData,
  premiumError,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const translate = useLanguageTranslate();

  // State for terms and promo code
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [promoCodeDiscount, setPromoCodeDiscount] = useState(0);
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [promoAppliedQuoteData, setPromoAppliedQuoteData] = useState(null);

  // Get loading state from Redux
  const paymentLoading = useSelector(getPolicyPayLoading);

  // Get profile data for Full Name and CPR
  const profileInfo = useSelector(selectProfileData);

  // Build display data
  const fullName = profileInfo
    ? `${profileInfo.FirstName || ""} ${profileInfo.LastName || ""}`.trim()
    : policyDetailsInfo?.Name || translate("N/A", "غير متوفر");

  const cpr =
    profileInfo?.CPR ||
    policyDetailsInfo?.CPR ||
    translate("N/A", "غير متوفر");

  // Get sum insured limit for display — show the value returned by the API
  // (View / Quote / Premium) in USD with thousand separators, e.g. "USD 500".
  const getSumInsuredLimitDisplay = () => {
    const apiSumInsured =
      premiumData?.SumInsured ??
      policyDetailsInfo?.SumInsured ??
      formData?.selectedPlan?.sumInsuredUSD;
    if (
      apiSumInsured === undefined ||
      apiSumInsured === null ||
      apiSumInsured === ""
    ) {
      return translate("N/A", "غير متوفر");
    }
    const amount = Number(apiSumInsured).toLocaleString("en-US");
    return translate(`USD ${amount}`, `${amount} دولار أمريكي`);
  };

  // Use pricing from premium data only — no view API fallback
  const hasPremium = !!premiumData && !premiumError;
  const basePremium = premiumData?.Premium || 0;
  const VAT = premiumData?.Tax || 0;
  const totalPrice = premiumData?.TotalPremium || (basePremium + VAT);
  const quoteNumber = premiumData?.QuoteNumber || premiumData?.PolicyNumber || policyDetailsInfo?.PolicyNumber;

  // Build planData for SummaryContent component — show N/A if premium API not available
  const planData = {
    basePremium: hasPremium ? basePremium : "N/A",
    premium: hasPremium ? basePremium : "N/A",
    tax: hasPremium ? VAT : "N/A",
    totalPremium: hasPremium ? totalPrice : "N/A",
    quoteNumber: quoteNumber || "N/A",
    policyNumber: policyDetailsInfo?.PolicyNumber || "N/A",
  };

  // Promo code handlers
  const applyPromoCode = async () => {
    setIsApplyingPromo(true);
    const result = await applyPromoCodeToQuote(quoteNumber, promoCode, translate);
    if (result.success) {
      setPromoAppliedQuoteData(result.data);
      setPromoCodeApplied(true);
      setPromoCodeDiscount(calculatePromoDiscount(totalPrice, result.data.TotalPremium));
    } else {
      toast.error(result.error);
    }
    setIsApplyingPromo(false);
  };

  const clearPromoCode = () => {
    setPromoCode("");
    setPromoCodeApplied(false);
    setPromoCodeDiscount(0);
    setPromoAppliedQuoteData(null);
  };

  // Handle payment - calls payment API only (premium already calculated)
  const handlePayment = async () => {
    if (!agreedToTerms) {
      toast.error(translate(
        "Please agree to the terms and conditions",
        "يرجى الموافقة على الشروط والأحكام"
      ));
      return;
    }

    const finalQuoteNumber = promoCodeApplied && promoAppliedQuoteData ? promoAppliedQuoteData.PolicyNumber : quoteNumber;
    const finalTotal = promoCodeApplied && promoAppliedQuoteData ? promoAppliedQuoteData.TotalPremium : totalPrice;

    try {
      // Build payment payload using premium data from previous screen
      const payload = {
        PolicyNumber: finalQuoteNumber,
        Premium: finalTotal,
        Channel: getClientSource(),
      };

      console.log("💳 Calling payment API:", payload);

      // Call payment API (NOT premium API)
      const paymentAction = await dispatch(policyPay(payload));
      const paymentLink = unwrapResult(paymentAction);

      console.log("✅ Payment API Response:", paymentLink);

      // Redirect to payment gateway
      if (paymentLink) {
        console.log("✅ Payment link received, redirecting...");
        window.open(paymentLink, "_self");
      } else {
        console.log("⚠️ No payment link, navigating to renewal hub");
        toast.success(
          translate(
            "Renewal processed successfully!",
            "تم معالجة التجديد بنجاح!"
          )
        );
        navigate("/renew");
      }

    } catch (error) {
      console.error("❌ Payment API error:", error);
      toast.error(
        translate(
          error.ErrorMessageEn || "Payment failed. Please try again.",
          error.ErrorMessageAr || "فشلت عملية الدفع. يرجى المحاولة مرة أخرى."
        )
      );
    }
  };

  return (
    <div className="mainCarDetailsModal pt-[1%] pb-[5%] max767:w-full max767:overflow-y-scroll max767:pb-[30%] max767:h-screen ipad:pt-[10%] ipad:h-[93vh] ipad:overflow-y-scroll">
      <RenewalLayout
        policyType="personalCyber"
        show_close_btn={false}
        is_hide_back_btn={false}
        backClickHandler={onBack}
        is_bottom_sticky_layout={true}
        show_summary={hasPremium}
        tempPrice={hasPremium ? totalPrice : undefined}
        btn_label={translate("Proceed to Payment", "المتابعة للدفع")}
        btn_onClick={handlePayment}
        btn_disabled={!agreedToTerms || !hasPremium || paymentLoading}
      >
        <div className="flex flex-col items-center mx-auto max-w-lg max767:px-4 pt-[0%]">
          <div className="flex justify-center items-center w-full">
            <div className="p-0 w-full">
              {/* Title and Description */}
              <div className="max767:mt-2">
                <TitleDescription
                  title={translate("Review and confirm", "المراجعة والتأكيد")}
                  description={translate(
                    "Please review your information and proceed to payment.",
                    "يرجى مراجعة معلوماتك والانتقال إلى الدفع."
                  )}
                />
              </div>

              {/* Policy Details Table */}
              <div className="mt-6">
                <div className="bg-[#F6F8F9] mb-6 p-4 max767:bg-white max767:p-0 rounded-2xl">
                  <h3 className="text-[#1A2024] rtl:max767:text-[#1A2024] text-base not-italic font-semibold rtl:max767:font-medium leading-6 tracking-[-0.16px]">
                    {translate(
                      "Personal Cyber Insurance Policy Renewal",
                      "تجديد سياسة التأمين السيبراني الشخصي"
                    )}
                  </h3>
                  <p className="text-[#1A2024] text-sm mb-4">
                    {translate(
                      "Review the details below and confirm the payment",
                      "راجع التفاصيل أدناه وأكد الدفع"
                    )}
                  </p>

                  {/* Details Table */}
                  <div className="max767:overflow-x-scroll">
                    <table className="min-w-full border-separate bg-white border border-solid border-[#DDE2E4] border-spacing-0 rounded-[10px]">
                      <tbody>
                        {/* 1. Full Name */}
                        <tr className="border-b">
                          <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm not-italic font-medium leading-6 h-[44px]">
                            {translate("Full Name", "الاسم الكامل")}
                          </td>
                          <td className="py-2 px-4 border-b text-[#1A2024] text-sm not-italic font-medium leading-6">
                            {fullName}
                          </td>
                        </tr>

                        {/* 2. CPR */}
                        <tr className="border-b">
                          <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm not-italic font-medium leading-6 h-[44px]">
                            {translate("CPR", "الرقم الشخصي")}
                          </td>
                          <td className="py-2 px-4 border-b text-[#1A2024] text-sm not-italic font-medium leading-6">
                            {cpr}
                          </td>
                        </tr>

                        {/* 3. Sum Insured Limit */}
                        <tr>
                          <td className="py-2 px-4 border-r rtl:border-l text-[#5B6871] text-sm not-italic font-medium leading-6 h-[44px]">
                            {translate("Sum Insured Limit", "مبلغ التأمين")}
                          </td>
                          <td className="py-2 px-4 text-[#1A2024] text-sm not-italic font-medium leading-6">
                            {getSumInsuredLimitDisplay()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Section - always show */}
          <div className="bg-[#F6F8F9] rounded-[26px] p-4 max767:bg-white max767:p-0 mb-6 w-full summary-box">
            <SummaryContent
              planData={promoCodeApplied && promoAppliedQuoteData ? { ...planData, premium: promoAppliedQuoteData.Premium ?? planData.premium, tax: promoAppliedQuoteData.Tax ?? planData.tax, totalPremium: promoAppliedQuoteData.TotalPremium ?? planData.totalPremium, quoteNumber: promoAppliedQuoteData.PolicyNumber ?? planData.quoteNumber } : planData}
              promoCode={promoCode}
              setPromoCode={setPromoCode}
              applyPromoCode={applyPromoCode}
              clearPromoCode={clearPromoCode}
              promoCodeApplied={promoCodeApplied}
              promoCodeDiscount={promoCodeDiscount}
              isApplyingPromo={isApplyingPromo}
              showQuotationNumber={true}
              showPromoCode={true}
              className="!bg-transparent"
              flowType="personalCyberRenewal"
            />
          </div>

          {/* Policy Notice */}
          <div className="mb-6 p-4 bg-[#FDE9D2] border border-[#FBC98E] rounded-lg flex items-start space-x-3 rtl:space-x-reverse w-full">
            <div className="flex-shrink-0 mt-0.5">
              <svg
                className="w-5 h-5 text-[#A56213]"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <p className="text-[#252C32] font-semibold text-sm">
                {translate(
                  "Kindly note that the policy will be issued under your name",
                  "يرجى ملاحظة أن الوثيقة ستصدر باسمك"
                )}
              </p>
            </div>
          </div>

          {/* Terms and Conditions */}
          <div className="mb-6 w-full max767:!pb-20">
            <label className="flex items-start space-x-3 rtl:space-x-reverse cursor-pointer">
              <label className="flex items-center cursor-pointer gap-2">
                <input
                  type="checkbox"
                  className="peer hidden"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                />
                <div
                  className="size-5 border-2 border-gray-400 rounded-[4px]
                    peer-checked:bg-[#00A9E7] peer-checked:border-[#00A9E7]
                    flex items-center justify-center transition-all"
                >
                  <svg
                    width="12"
                    height="9"
                    viewBox="0 0 12 9"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M11 0C10.72 0 10.47 0.11 10.29 0.29L4 6.59L1.71 4.29C1.53 4.11 1.28 4 1 4C0.45 4 0 4.45 0 5C0 5.28 0.11 5.53 0.29 5.71L3.29 8.71C3.47 8.89 3.72 9 4 9C4.28 9 4.53 8.89 4.71 8.71L11.71 1.71C11.89 1.53 12 1.28 12 1C12 0.45 11.55 0 11 0Z"
                      fill="white"
                    />
                  </svg>
                </div>
              </label>
              <div className="text-sm text-[#252C32] rtl:font-medium leading-[24px] text-justify">
                <span>
                  {translate(
                    "I hereby certify that the above given information is true and correct to the best of my knowledge, and I agree to cooperate fully by providing all requested documents before payment. I understand that providing invalid information will result in my insurance policy to be void. I also read and agree the",
                    "أشهد بموجب هذا أن المعلومات المقدمة أعلاه صحيحة ودقيقة حسب أفضل ما أعلم، وأوافق على التعاون الكامل من خلال تقديم جميع الوثائق المطلوبة قبل الدفع. أفهم أن تقديم معلومات غير صحيحة سيؤدي إلى إبطال وثيقة التأمين الخاصة بي. كما أنني قرأت وأوافق على"
                  )}
                </span>{" "}
                <button
                  onClick={() =>
                    window.open(
                      "https://content.solidarity.bh/uploads/Personal_Cyber_Insurance_0fcdb73314.pdf",
                      "_blank"
                    )
                  }
                  className="text-[#00384D] underline hover:text-[#0088CC] font-bold"
                >
                  {translate("Policy Specification", "مواصفات وثيقة")}
                </button>{" "}
                <span>
                  {translate("of this Insurance product.", "التأمين هذه.")}
                </span>
              </div>
            </label>
          </div>

        </div>
      </RenewalLayout>
    </div>
  );
};

export default ReviewConfirmPage;
